import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Send, Users, Shield, Clock, FileText } from 'lucide-react';
import api from '../../../utils/api';
import MainLayout from '../../../components/layout/MainLayout';
import FilteredLeadSelectTable from '../../../components/automation/FilteredLeadSelectTable';
import SchedulerPicker from '../../../components/automation/SchedulerPicker';

const WWebBulkCampaignWizard = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Variable Mapping State
  const [templateVariables, setTemplateVariables] = useState([]);
  const [variableMappings, setVariableMappings] = useState([]);
  const [latestSelectedLeadData, setLatestSelectedLeadData] = useState(null);
  const [allLoadedLeads, setAllLoadedLeads] = useState([]);
  const [customFieldDefs, setCustomFieldDefs] = useState([]);
  const [dynamicLeadFields, setDynamicLeadFields] = useState([]);

  useEffect(() => {
    const fetchCustomFields = async () => {
      try {
        const res = await api('/custom-fields');
        if (res.ok) setCustomFieldDefs(await res.json());
      } catch (e) {
        console.error('Failed to fetch custom fields:', e);
      }
    };
    fetchCustomFields();
  }, []);

  const buildLeadFields = (lead, customFields, allLeads) => {
    let standard = [];
    let leadCustomFields = [];
    const keysToIgnore = ['_id', '__v', 'customFields', 'logs', 'followUps', 'tenantId', 'addedBy', 'assignedTelecaller', 'assignedExecutive', 'assignedBy', 'updatedAt', 'createdAt', 'addedByName', 'assignedTelecallerName', 'assignedExecutiveName', 'assignedByName', 'updatedBy', 'updatedByName'];

    const extractKeys = (l) => {
      Object.keys(l).forEach(key => {
        if (!keysToIgnore.includes(key) && typeof l[key] !== 'object') {
          const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
          if (!standard.find(s => s.value === key)) {
            standard.push({ value: key, label: label });
          }
        }
      });

      if (l.customFields && typeof l.customFields === 'object') {
        Object.keys(l.customFields).forEach(key => {
          const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
          if (!leadCustomFields.find(c => c.value === `customFields.${key}`)) {
            leadCustomFields.push({ value: `customFields.${key}`, label: `${label} (Custom)` });
          }
        });
      }
    };

    if (lead) extractKeys(lead);
    if (allLeads && allLeads.length > 0) {
      allLeads.forEach(extractKeys);
    }

    if (standard.length === 0) {
      standard = [
        { value: 'name', label: 'Name' },
        { value: 'phone', label: 'Phone' },
        { value: 'email', label: 'Email' },
        { value: 'location', label: 'Location' },
        { value: 'propertyInterest', label: 'Property Interest' },
        { value: 'budget', label: 'Budget' },
        { value: 'leadType', label: 'Lead Type' },
        { value: 'status', label: 'Status' },
        { value: 'source', label: 'Source' }
      ];
    }
    
    const defs = (customFields || []).map(cf => ({
      value: `customFields.${cf.name}`,
      label: cf.name + ' (Custom)'
    }));

    // Merge custom fields from definition and from lead object without duplicates
    const allCustom = [...leadCustomFields];
    defs.forEach(def => {
       if (!allCustom.find(c => c.value === def.value)) {
           allCustom.push(def);
       }
    });

    return [...standard, ...allCustom];
  };

  useEffect(() => {
    setDynamicLeadFields(buildLeadFields(latestSelectedLeadData, customFieldDefs, allLoadedLeads));
  }, [latestSelectedLeadData, customFieldDefs, allLoadedLeads]);
  
  // Anti-ban Settings
  const [batchSize, setBatchSize] = useState(10);
  const [delayMinMs, setDelayMinMs] = useState(8000);
  const [delayMaxMs, setDelayMaxMs] = useState(20000);
  const [cooldownBetweenBatchesMs, setCooldownBetweenBatchesMs] = useState(120000);
  const [simulateTyping, setSimulateTyping] = useState(true);

  // Schedule Settings
  const [scheduleMode, setScheduleMode] = useState('now');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (step === 2 && templates.length === 0) {
      fetchTemplates();
    }
  }, [step]);

  const fetchTemplates = async () => {
    try {
      const res = await api('/wa-web-templates');
      if (res.ok) {
        const data = await res.json();
        setTemplates(data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (selectedTemplate) {
      const vars = selectedTemplate.variables || [];
      setTemplateVariables(vars);
      setVariableMappings(vars.map(v => ({
        placeholder: v.placeholder,
        source: v.defaultSource || 'lead_field',
        leadField: v.defaultLeadField || '',
        staticValue: v.defaultStaticValue || ''
      })));
    } else {
      setTemplateVariables([]);
      setVariableMappings([]);
    }
  }, [selectedTemplate]);

  const hasVariables = templateVariables.length > 0;
  const TOTAL_STEPS = hasVariables ? 5 : 4;

  const handleNext = () => {
    if (step === 1 && selectedLeads.length === 0) return alert('Select at least one lead');
    if (step === 2 && !selectedTemplate) return alert('Select a template');
    
    if (step === 3 && hasVariables) {
      if (variableMappings.some(m => m.source === 'lead_field' && !m.leadField)) {
        return alert('Please select a lead field for all variables mapped from lead.');
      }
    }
    
    if (step === 2 && !hasVariables) {
      setStep(4); // Skip Variables
    } else {
      setStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (step === 4 && !hasVariables) {
      setStep(2);
    } else {
      setStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (scheduleMode === 'schedule' && (!scheduledDate || !scheduledTime)) {
      return alert('Select date and time');
    }

    setIsSubmitting(true);
    let scheduledAt = null;
    if (scheduleMode === 'schedule') {
      scheduledAt = new Date(`${scheduledDate}T${scheduledTime}`).toISOString();
    }

    try {
      const res = await api('/wa-web-automation/bulk-send', {
        method: 'POST',
        body: {
          templateId: selectedTemplate._id,
          variableMappings,
          leadIds: selectedLeads,
          batchSize,
          delayMinMs,
          delayMaxMs,
          cooldownBetweenBatchesMs,
          simulateTyping,
          scheduledAt
        }
      });
      
      if (!res.ok) throw new Error('Failed to create campaign');

      alert('Campaign created successfully!');
      navigate('/automation/whatsapp-web/bulk/jobs');
    } catch (e) {
      alert('Error creating campaign: ' + e.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper to calculate estimated time
  const estimatedSeconds = ((delayMinMs + delayMaxMs) / 2 / 1000) * selectedLeads.length + 
    (Math.ceil(selectedLeads.length / batchSize) - 1) * (cooldownBetweenBatchesMs / 1000);
  const estimatedTimeStr = estimatedSeconds < 60 
    ? `${Math.round(estimatedSeconds)} seconds` 
    : `${Math.round(estimatedSeconds / 60)} minutes`;

  return (
    <MainLayout>
      <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>
        <button 
          onClick={() => navigate('/automation/whatsapp-web/bulk')}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', padding: 0, fontSize: '14px', marginBottom: '24px' }}
        >
          <ArrowLeft size={16} /> Back
        </button>

        <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>New WhatsApp Web Campaign</h1>
        
        {/* Progress Bar */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '32px' }}>
          {[1, 2, 3, 4, 5].map(s => {
            if (s === 3 && !hasVariables) return null; // Skip drawing variable step
            const isActive = step >= s;
            return <div key={s} style={{ flex: 1, height: '6px', background: isActive ? 'var(--color-brand-primary)' : 'var(--color-border-primary)', borderRadius: '3px' }} />;
          })}
        </div>

        <div style={{ background: 'white', padding: '24px', borderRadius: '12px', border: '1px solid var(--color-border-primary)', minHeight: '400px' }}>
          
          {/* STEP 1: LEADS */}
          {step === 1 && (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>Select Target Audience</h3>
              <FilteredLeadSelectTable 
                selectedLeads={selectedLeads} 
                setSelectedLeads={setSelectedLeads} 
                onLeadDataChange={(lead, allLeads) => {
                  setLatestSelectedLeadData(lead);
                  setAllLoadedLeads(allLeads || []);
                }}
              />
            </div>
          )}

          {/* STEP 2: TEMPLATE */}
          {step === 2 && (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>Choose Template</h3>
              {templates.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center' }}>No templates found. Go to Template Manager to create one.</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
                  {templates.map(t => (
                    <div 
                      key={t._id} 
                      onClick={() => setSelectedTemplate(t)}
                      style={{ 
                        border: `2px solid ${selectedTemplate?._id === t._id ? 'var(--color-brand-primary)' : 'var(--color-border-primary)'}`,
                        borderRadius: '8px', padding: '16px', cursor: 'pointer' 
                      }}
                    >
                      <div style={{ fontWeight: '600', marginBottom: '8px' }}>{t.name}</div>
                      <div style={{ fontSize: '14px', color: 'var(--color-text-secondary)', whiteSpace: 'pre-wrap' }}>{t.body}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* STEP 3: VARIABLES */}
          {step === 3 && hasVariables && (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>Map Variables</h3>
              {templateVariables.map(v => (
                 <div key={v.placeholder} style={{ marginBottom: '16px', border: '1px solid var(--color-border-primary)', padding: '16px', borderRadius: '8px' }}>
                    <div style={{ fontWeight: '500', marginBottom: '12px' }}>{`{{${v.placeholder}}}`}</div>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <select 
                        value={variableMappings.find(m => m.placeholder === v.placeholder)?.source || 'lead_field'}
                        onChange={(e) => {
                          const updated = [...variableMappings];
                          const idx = updated.findIndex(m => m.placeholder === v.placeholder);
                          updated[idx].source = e.target.value;
                          setVariableMappings(updated);
                        }}
                        style={{ padding: '8px', borderRadius: '6px', border: '1px solid var(--color-border-primary)' }}
                      >
                        <option value="lead_field">Lead Field</option>
                        <option value="static">Static Value</option>
                      </select>
                      
                      {variableMappings.find(m => m.placeholder === v.placeholder)?.source === 'lead_field' ? (
                        <select 
                          value={variableMappings.find(m => m.placeholder === v.placeholder)?.leadField || ''}
                          onChange={(e) => {
                            const updated = [...variableMappings];
                            const idx = updated.findIndex(m => m.placeholder === v.placeholder);
                            updated[idx].leadField = e.target.value;
                            setVariableMappings(updated);
                          }}
                          style={{ padding: '8px', borderRadius: '6px', border: '1px solid var(--color-border-primary)', flex: 1 }}
                        >
                          <option value="">Select Field...</option>
                          {dynamicLeadFields.map(field => (
                            <option key={field.value} value={field.value}>{field.label}</option>
                          ))}
                        </select>
                      ) : (
                        <input 
                          type="text" 
                          placeholder="Enter static value"
                          value={variableMappings.find(m => m.placeholder === v.placeholder)?.staticValue || ''}
                          onChange={(e) => {
                            const updated = [...variableMappings];
                            const idx = updated.findIndex(m => m.placeholder === v.placeholder);
                            updated[idx].staticValue = e.target.value;
                            setVariableMappings(updated);
                          }}
                          style={{ padding: '8px', borderRadius: '6px', border: '1px solid var(--color-border-primary)', flex: 1 }}
                        />
                      )}
                    </div>
                 </div>
              ))}
            </div>
          )}

          {/* STEP 4: ANTI-BAN */}
          {step === 4 && (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Shield size={20} color="var(--color-brand-primary)" /> Anti-Ban Configuration
              </h3>
              <p style={{ color: 'var(--color-text-secondary)', marginBottom: '24px', fontSize: '14px' }}>Configure these settings to keep your WhatsApp account safe.</p>
              
              <div style={{ display: 'grid', gap: '24px', maxWidth: '600px' }}>
                <div>
                  <label style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontWeight: '500' }}>Batch Size</span>
                    <span>{batchSize} messages</span>
                  </label>
                  <input type="range" min="1" max="50" value={batchSize} onChange={e => setBatchSize(Number(e.target.value))} style={{ width: '100%' }} />
                </div>
                
                <div>
                  <label style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontWeight: '500' }}>Delay Between Messages (Min - Max seconds)</span>
                    <span>{delayMinMs/1000}s - {delayMaxMs/1000}s</span>
                  </label>
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <input type="number" value={delayMinMs/1000} onChange={e => setDelayMinMs(Number(e.target.value)*1000)} min="1" max="60" style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid var(--color-border-primary)' }} />
                    <span style={{ alignSelf: 'center' }}>to</span>
                    <input type="number" value={delayMaxMs/1000} onChange={e => setDelayMaxMs(Number(e.target.value)*1000)} min="2" max="120" style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid var(--color-border-primary)' }} />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontWeight: '500' }}>Cooldown Between Batches (Minutes)</span>
                    <span>{cooldownBetweenBatchesMs/60000} min</span>
                  </label>
                  <input type="range" min="1" max="10" step="1" value={cooldownBetweenBatchesMs/60000} onChange={e => setCooldownBetweenBatchesMs(Number(e.target.value)*60000)} style={{ width: '100%' }} />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: 'var(--color-background-secondary)', padding: '16px', borderRadius: '8px' }}>
                  <input type="checkbox" id="simTyping" checked={simulateTyping} onChange={e => setSimulateTyping(e.target.checked)} style={{ width: '18px', height: '18px' }} />
                  <label htmlFor="simTyping" style={{ fontWeight: '500', cursor: 'pointer' }}>Simulate "Typing..." before sending</label>
                </div>
              </div>
            </div>
          )}

          {/* STEP 5: SCHEDULE & CONFIRM */}
          {step === 5 && (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>Confirm & Schedule</h3>
              
              <div style={{ background: 'var(--color-background-secondary)', padding: '20px', borderRadius: '12px', marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <Users size={20} color="var(--color-text-secondary)" />
                  <span style={{ fontWeight: '500' }}>Total Leads:</span> {selectedLeads.length}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <FileText size={20} color="var(--color-text-secondary)" />
                  <span style={{ fontWeight: '500' }}>Template:</span> {selectedTemplate?.name}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Clock size={20} color="var(--color-text-secondary)" />
                  <span style={{ fontWeight: '500' }}>Est. Time to Complete:</span> ~{estimatedTimeStr}
                </div>
              </div>

              <SchedulerPicker 
                scheduleMode={scheduleMode} setScheduleMode={setScheduleMode}
                scheduledDate={scheduledDate} setScheduledDate={setScheduledDate}
                scheduledTime={scheduledTime} setScheduledTime={setScheduledTime}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px' }}>
          <button 
            onClick={handleBack} 
            disabled={step === 1 || isSubmitting}
            style={{ padding: '12px 24px', borderRadius: '8px', border: '1px solid var(--color-border-primary)', background: 'white', cursor: step===1 ? 'not-allowed' : 'pointer', opacity: step===1 ? 0.5 : 1, fontWeight: '500' }}
          >
            Back
          </button>
          
          {step < TOTAL_STEPS ? (
            <button 
              onClick={handleNext}
              style={{ padding: '12px 24px', borderRadius: '8px', border: 'none', background: 'var(--color-brand-primary)', color: 'white', cursor: 'pointer', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              Next <ArrowRight size={18} />
            </button>
          ) : (
            <button 
              onClick={handleSubmit}
              disabled={isSubmitting}
              style={{ padding: '12px 24px', borderRadius: '8px', border: 'none', background: 'var(--color-brand-primary)', color: 'white', cursor: isSubmitting ? 'wait' : 'pointer', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px', opacity: isSubmitting ? 0.7 : 1 }}
            >
              {isSubmitting ? 'Starting...' : 'Launch Campaign'} <Send size={16} />
            </button>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default WWebBulkCampaignWizard;
